import { z } from "zod";
import { ProposedEncounterLabOrderSchema } from "./agent.js";
import { ActionBundleReviewSchema } from "./atomic-acceptance.js";
import { DocumentSelectionDecisionSchema, GeneratedDocumentArtifactSchema, } from "./clinic-documents.js";
import { PrepareLabOrderDraftRequestSchema } from "./lab-order-drafts.js";
import { refineManualOrderRunKey } from "./manual-order-marker.js";
import { ManualOrderManualValuesSchema } from "./manual-orders.js";
import { IdempotencyKeySchema } from "./primitives.js";
import { AgentExecutionModeSchema, RolloutPolicyReferenceSchema } from "./rollout-governance.js";
/** What only a "+" order's preparation sends: the forms as the physician completed them. */
const MANUAL_ORDER_PREPARATION_KEYS = [
    "manualValues",
    "declinedConceptIds",
    "placedTests",
    "previewedForms",
];
/**
 * One-call governed laboratory-order preparation.
 *
 * The browser used to drive this as six sequential requests (save note → start
 * agent run → stream events → fetch review → select clinic document → generate
 * PDF). The server now runs the same governed steps in order and answers once.
 * The request carries exactly what the agent run needs; the encounter comes
 * from the route path.
 *
 * A "+" order (a proposal carrying `manualOrder`) also sends what the physician completed on its
 * forms, which a plan preparation reads from its saved draft instead, and runs under its own key.
 * A plan request never carries those fields or that key, so it parses and hashes as before.
 */
export const PrepareLabOrderRequestSchema = z
    .object({
    expectedEncounterVersion: z.number().int().positive(),
    proposedLabOrder: ProposedEncounterLabOrderSchema,
    idempotencyKey: IdempotencyKeySchema,
    /** "+" only: values typed on the forms, keyed `<documentId>:<fieldId>` as previews send them. */
    manualValues: ManualOrderManualValuesSchema.optional(),
    /** "+" only: tests clinic instructions suggested that the physician chose not to order. */
    declinedConceptIds: PrepareLabOrderDraftRequestSchema.shape.declinedConceptIds,
    /**
     * "+" only, empty at first: an ordered test no form field shows then comes back listed for
     * the physician to add by hand instead of refusing. Its entries are the tests they confirmed.
     */
    placedTests: PrepareLabOrderDraftRequestSchema.shape.placedTests,
    /**
     * "+" only: the forms on screen, in order. A prepared set that is exactly these comes back
     * without the editor fields, which the physician already has.
     */
    previewedForms: PrepareLabOrderDraftRequestSchema.shape.previewedForms,
})
    .strict()
    .superRefine((request, context) => {
    if (request.proposedLabOrder.manualOrder === undefined) {
        for (const key of MANUAL_ORDER_PREPARATION_KEYS) {
            if (request[key] !== undefined) {
                context.addIssue({
                    code: "custom",
                    path: [key],
                    message: "Only a manual order sends what was completed on its forms",
                });
            }
        }
    }
    refineManualOrderRunKey(request.proposedLabOrder.manualOrder, request.idempotencyKey, context);
});
export const PreparedLabOrderPreviewSchema = z
    .object({
    selection: DocumentSelectionDecisionSchema,
    artifacts: z.array(GeneratedDocumentArtifactSchema).min(1).max(50),
})
    .strict();
/**
 * Mode decides what the server may hand back: `shadow` records the run and
 * exposes nothing; `draft_only` exposes the exact review but no executable
 * PDF; `approved_execution` returns the review and the ready-to-sign draft.
 */
export const PreparedLabOrderSchema = z
    .object({
    runId: z.string().uuid(),
    mode: AgentExecutionModeSchema,
    rolloutPolicy: RolloutPolicyReferenceSchema,
    review: ActionBundleReviewSchema.nullable(),
    preview: PreparedLabOrderPreviewSchema.nullable(),
})
    .strict()
    .superRefine((prepared, context) => {
    if (prepared.mode === "shadow" && (prepared.review !== null || prepared.preview !== null)) {
        context.addIssue({
            code: "custom",
            path: ["review"],
            message: "A shadow run must not expose a review or a preview",
        });
    }
    if (prepared.mode === "draft_only" && prepared.preview !== null) {
        context.addIssue({
            code: "custom",
            path: ["preview"],
            message: "A draft-only run must not expose an executable PDF preview",
        });
    }
    if (prepared.mode !== "shadow" && prepared.review === null) {
        context.addIssue({
            code: "custom",
            path: ["review"],
            message: "A reviewable run must carry its exact review",
        });
    }
});
//# sourceMappingURL=lab-order-governance.js.map